<template>
  <div class="p-6 space-y-8">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <div>
        <h2 class="text-2xl font-bold text-gray-800 mb-2">
          {{ isEditing ? 'Modifier la règle' : 'Créer une nouvelle règle' }}
        </h2>
        <p class="text-gray-600">
          Configurez les règles de gain d'XP et de points
        </p>
      </div>
    </div>

    <form @submit.prevent ref="form" class="space-y-8">
      <!-- Informations principales -->
      <div class="bg-white rounded-xl border border-gray-200 p-6">
        <h3 class="text-lg font-semibold text-gray-800 mb-6 flex items-center">
          <i class="pi pi-info-circle mr-2 text-blue-500"></i>
          Informations générales
        </h3>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Nom de la règle -->
          <div>
            <label class="mb-2 block text-sm font-medium text-gray-700">
              Nom de la règle <span class="text-red-500">*</span>
            </label>
            <input
              type="text"
              v-model="rule.name"
              required
              class="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Ex: Commande terminée"
            />
          </div>

          <!-- Evénement -->
          <div>
            <label class="mb-2 block text-sm font-medium text-gray-700">
              Événement <span class="text-red-500">*</span>
            </label>
            <select
              v-model="rule.event"
              @change="onEventChange"
              required
              class="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="" disabled>Sélectionner un événement</option>
              <option v-for="evt in AVAILABLE_EVENTS" :key="evt.key" :value="evt.key">
                {{ evt.label }}
              </option>
            </select>
          </div>
        </div>

        <!-- Description -->
        <div class="mt-6">
          <label class="mb-2 block text-sm font-medium text-gray-700">
            Description
          </label>
          <textarea
            v-model="rule.description"
            rows="3"
            class="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Décrivez cette règle..."
          ></textarea>
        </div>
      </div>

      <!-- Récompenses -->
      <div class="bg-white rounded-xl border border-gray-200 p-6">
        <h3 class="text-lg font-semibold text-gray-800 mb-6 flex items-center">
          <i class="pi pi-star mr-2 text-yellow-500"></i>
          Récompenses
        </h3>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label class="mb-2 block text-sm font-medium text-gray-700">
              XP gagnée <span class="text-red-500">*</span>
            </label>
            <input
              type="number"
              v-model.number="rule.xp"
              min="0"
              required
              class="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Ex: 100"
            />
          </div>
          <div>
            <label class="mb-2 block text-sm font-medium text-gray-700">
              Points gagnés <span class="text-red-500">*</span>
            </label>
            <input
              type="number"
              v-model.number="rule.points"
              min="0"
              required
              class="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Ex: 50"
            />
          </div>
        </div>
        

        
        <div class="mt-4 flex items-center">
          <input
            type="checkbox"
            id="ruleActive"
            v-model="rule.isActive"
            class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <label for="ruleActive" class="ml-2 block text-sm text-gray-700">
            Règle active
          </label>
        </div>
      </div>

      <!-- Boutons d'action -->
      <div class="flex justify-end space-x-4">
        <button
          type="button"
          @click="$emit('cancel')"
          class="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
        >
          Annuler
        </button>
        <button
          type="button"
          @click="saveRule"
          class="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-200 flex items-center space-x-2"
        >
          <i class="pi pi-save"></i>
          <span>{{ isEditing ? 'Mettre à jour' : 'Créer' }} la règle</span>
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, reactive, watch, computed, onMounted } from 'vue'

const props = defineProps({
  ruleData: {
    type: Object,
    default: () => ({}),
  },
})

const emit = defineEmits(['saved', 'cancel'])

const AVAILABLE_EVENTS = [
  { key: 'ORDER_COMPLETED', label: 'Commande terminée', stats: { ordersCompleted: 1 } },
  // { key: 'PROFILE_COMPLETED', label: 'Profil complété', stats: { profileCompleted: 1 } },
  { key: 'REVIEW_CREATED', label: 'Avis publié', stats: { reviewsCreated: 1 } },
  // { key: 'FAVORITE_ADDED', label: 'Favori ajouté', stats: { favoritesAdded: 1 } },
  // { key: 'BIG_ORDER_COMPLETED', label: 'Grosse commande terminée', stats: {} },
]

const form = ref(null)

const isEditing = computed(
  () => props.ruleData && Object.keys(props.ruleData).length > 0 && props.ruleData.id
)

const rule = reactive({
  id: '',
  event: '',
  name: '',
  description: '',
  xp: 0,
  points: 0,
  isActive: true,
})

const statsString = ref('')

// Watcher pour les données initiales
watch(
  () => props.ruleData,
  newData => {
    if (newData && Object.keys(newData).length > 0) {
      Object.assign(rule, {
        id: '',
        event: '',
        name: '',
        description: '',
        xp: 0,
        points: 0,
        isActive: true,
        ...newData,
      })
      if (newData.stats) {
        statsString.value = JSON.stringify(newData.stats)
      } else {
        statsString.value = ''
      }
    }
  },
  { immediate: true, deep: true },
)

function onEventChange() {
  const selected = AVAILABLE_EVENTS.find(e => e.key === rule.event)
  if (selected) {
    if (!rule.name) {
      rule.name = selected.label
    }
    statsString.value = JSON.stringify(selected.stats)
  }
}

// Méthodes
const saveRule = () => {
  const isValid = form.value.checkValidity()
  if (!isValid) {
    form.value.reportValidity()
    return
  }

  let statsObj = null
  try {
    if (statsString.value.trim()) {
      statsObj = JSON.parse(statsString.value)
    }
  } catch (e) {
    alert("Le JSON des stats est invalide.")
    return
  }

  // Nettoyer et valider les données
  const cleanRule = { 
    ...rule,
    stats: statsObj,
    operation: isEditing.value ? 'update' : 'add'
  }

  emit('saved', cleanRule)
}
</script>

<style scoped>
</style>
